// src/components/AIAssistantManagement.js — /ai-assistants?assistantId=<id>,
// one AI agent. The backend's ticket journey, the copilot and onboarding link
// here with the agent's _id: the param is kept and read on every change.
// Header (version, actions: knowledge, edit, ⋯ default / delete) → « À vérifier » (no
// instructions, reference set failing, tools no longer offered) → tiles over
// the period (runs, drafts approved, carrier messages validated, statuses
// taken back: GET /api/ai-runs/assistants/{id}/versions; reference set: GET
// /api/ai-eval/runs) → views (?tab=): instructions (+ structured rules),
// tools (+ the account's policy of each effectful tool), versions (+
// evaluations), reference set. ?days= sets the period.
// Writes: PUT /api/ai-assistants/{id} {name, prompt, selected_tool_ids};
// PUT /api/ai-assistants/{id}/running (default agent) and DELETE
// /api/ai-assistants/{id}, each behind a confirmation; PUT
// /api/ai-proposals/policies/{tool} {mode, conditions}; the rules, the
// reference set and the evaluations write from their components. The test
// chat is gone: its /api/webhook/chatbot/test routes were removed (Oct 2025).
// Other reads: GET /api/users/user, /api/ai-assistants, /api/tools,
// /api/ai-assistants/{id}, /api/ai-proposals/policies.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BookOpen, Bot, Check, ChevronRight, Copy, FlaskConical, History, ListChecks, MessagesSquare,
  Pencil, ScrollText, Sparkles, Star, Trash2, TriangleAlert, Truck, Wrench,
} from 'lucide-react';
import {
  Badge, Banner, Card, ConfirmDialog, DescriptionList, EmptyState, Field, Kpi, OverflowMenu, PageHeader, Tabs, Toast,
} from '../design/ui';
import AssistantVersions, { VERSION_PERIODS } from './AssistantVersions';
import AssistantRules, { AssistantHarness } from './AssistantRules';
import ToolChecklist, { ToolBadges } from './AIAssistantTools';
import {
  activeChannels, agentTools, authHeaders, channelLabel, currentVersionEntry, formatCount, formatPercent,
  formatRelative, formatShortDay, formatStamp, groupTools, hasInstructions, parsePromptBlocks, summarizeVersions,
  toolDescription, toolFunctionName, toolGroupMeta, toolLabel, wordCount,
} from './aiAgentsShared';
import { DEMO_AGENTS, DEMO_TOOLS } from './aiAgentsDemo';
import './AIAssistantManagement.css';

const VIEWS = ['instructions', 'tools', 'versions', 'evaluation'];
const DEFAULT_DAYS = 90;

// **bold** and `code` inside a line of the instructions.
const renderInline = (text) => {
  const parts = [];
  const regex = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0;
  let match;
  let key = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    const token = match[0];
    if (token.startsWith('**')) parts.push(<strong key={key++}>{token.slice(2, -2)}</strong>);
    else parts.push(<code key={key++} className="u-code">{token.slice(1, -1)}</code>);
    last = match.index + token.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
};

const renderBlock = (block, index) => {
  switch (block.type) {
    case 'heading':
      return (
        <h3 key={index} id={`prompt-sec-${block.num}`} className="aiam-v2__doc-heading">
          <span className="aiam-v2__doc-heading-num">{block.num}</span>
          {block.text}
        </h3>
      );
    case 'rule':
      return (
        <p key={index} className="aiam-v2__doc-rule">
          <span className="aiam-v2__doc-rule-num u-num">{block.num}</span>
          <span>{renderInline(block.text)}</span>
        </p>
      );
    case 'bullet':
      return (
        <p key={index} className="aiam-v2__doc-bullet">
          <span className="aiam-v2__doc-dot" aria-hidden="true" />
          <span>{renderInline(block.text)}</span>
        </p>
      );
    case 'hr':
      return <hr key={index} className="aiam-v2__doc-hr" />;
    case 'tag':
      return <div key={index} className="aiam-v2__doc-tag">{block.text}</div>;
    case 'space':
      return <div key={index} className="aiam-v2__doc-space" />;
    default:
      return <p key={index} className="aiam-v2__doc-p">{renderInline(block.text)}</p>;
  }
};

const AIAssistantManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlId = searchParams.get('assistantId');
  const view = VIEWS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'instructions';
  const daysParam = Number(searchParams.get('days'));
  const days = VERSION_PERIODS.includes(daysParam) ? daysParam : DEFAULT_DAYS;

  const [user, setUser] = useState(undefined);
  const [agents, setAgents] = useState(null);
  const [tools, setTools] = useState([]);
  const [toolPolicies, setToolPolicies] = useState({}); // tool → tenant mode
  const [policyConditions, setPolicyConditions] = useState({}); // tool → stored conditions (sent back untouched)
  const [policyMeta, setPolicyMeta] = useState({}); // tool → { effect, default_policy, human_allowed }
  const [policySaving, setPolicySaving] = useState(null); // tool being saved
  const [selected, setSelected] = useState(null);
  const [missing, setMissing] = useState(false);
  // Monotonic id of the latest detail fetch, so a slow answer for a previous
  // agent never overwrites the current one.
  const detailRequestRef = useRef(0);

  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editedPrompt, setEditedPrompt] = useState('');
  const [editedToolIds, setEditedToolIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const [versionsData, setVersionsData] = useState(undefined);
  const [versionsError, setVersionsError] = useState(null);
  const [evalData, setEvalData] = useState({ rules: null, cases: null, reports: null, error: null });

  const [confirmAction, setConfirmAction] = useState(null); // null | 'default' | 'delete'
  const [actionBusy, setActionBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const demo = Boolean(user && user.role === 'experience');

  const showToast = (tone, text) => setToast({ tone, text });
  useEffect(() => {
    if (!toast || toast.tone === 'danger') return undefined;
    const timer = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(timer);
  }, [toast]);

  const updateParams = useCallback((updates) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') next.delete(key);
        else next.set(key, String(value));
      });
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // ---- loading ----------------------------------------------------------------

  const fetchList = useCallback(async () => {
    try {
      const [assistantsResponse, toolsResponse] = await Promise.all([
        axios.get('/api/ai-assistants', { headers: authHeaders() }),
        axios.get('/api/tools', { headers: authHeaders() }),
      ]);
      setAgents(Array.isArray(assistantsResponse.data) ? assistantsResponse.data : []);
      setTools(Array.isArray(toolsResponse.data) ? toolsResponse.data : []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setAgents((previous) => previous || []);
    }
  }, []);

  useEffect(() => {
    if (!localStorage.getItem('access_token')) {
      navigate('/');
      return;
    }
    (async () => {
      try {
        const response = await axios.get('/api/users/user', { headers: authHeaders() });
        setUser(response.data);
        if (response.data && response.data.role === 'experience') {
          setAgents(DEMO_AGENTS);
          setTools(DEMO_TOOLS);
        } else {
          await fetchList();
        }
      } catch (error) {
        console.error('Failed to fetch user data:', error);
        setUser(null);
      }
    })();
  }, [navigate, fetchList]);

  useEffect(() => {
    if (user === null) navigate('/');
  }, [user, navigate]);

  // Registry policies (plan H2): what each effectful tool does on this
  // account — the tenant overrides and, per effectful tool, the registry
  // default the editor of the « Outils » view offers.
  useEffect(() => {
    axios.get('/api/ai-proposals/policies', { headers: authHeaders() })
      .then((response) => {
        const modes = {};
        const conditions = {};
        ((response.data && response.data.policies) || []).forEach((policy) => {
          modes[policy.tool] = policy.mode;
          conditions[policy.tool] = policy.conditions || {};
        });
        const meta = {};
        ((response.data && response.data.tools) || []).forEach((tool) => { meta[tool.name] = tool; });
        setToolPolicies(modes);
        setPolicyConditions(conditions);
        setPolicyMeta(meta);
      })
      .catch(() => { /* optional: the registry defaults stay, no editor */ });
  }, []);

  // No agent in the URL: the default agent (or the first) is shown, and named in the URL.
  useEffect(() => {
    if (!agents || urlId || !agents.length) return;
    const running = agents.find((agent) => agent.is_running);
    updateParams({ assistantId: (running || agents[0])._id });
  }, [agents, urlId, updateParams]);

  const loadSelected = useCallback(async (id) => {
    const summary = (agents || []).find((agent) => agent._id === id) || null;
    if (demo || !id) {
      setSelected(summary);
      setMissing(!summary);
      return;
    }
    if (summary) setSelected((current) => (current && current._id === id ? current : summary));
    const requestId = ++detailRequestRef.current;
    try {
      const response = await axios.get(`/api/ai-assistants/${id}`, { headers: authHeaders() });
      if (requestId !== detailRequestRef.current) return;
      if (response.data && response.data._id) {
        setSelected(response.data);
        setMissing(false);
      }
    } catch (error) {
      console.error('Failed to fetch AI assistant details:', error);
      if (requestId !== detailRequestRef.current) return;
      if (!summary) setMissing(true); // the list summary keeps the page usable otherwise
    }
  }, [agents, demo]);

  useEffect(() => {
    if (!agents || !urlId || !agents.length) return;
    setIsEditing(false);
    setMissing(false);
    loadSelected(urlId);
    // Only a change of agent (or of the list) reloads the detail.
  }, [agents, urlId]); // eslint-disable-line react-hooks/exhaustive-deps

  const localId = selected ? (selected.assistant_id || selected._id) : null;

  const loadVersions = useCallback(async () => {
    if (!localId || demo) return;
    setVersionsError(null);
    try {
      const response = await axios.get(`/api/ai-runs/assistants/${localId}/versions?days=${days}`, { headers: authHeaders() });
      setVersionsData(response.data || { versions: [] });
    } catch (error) {
      setVersionsData({ versions: [] });
      setVersionsError(t('assistantVersions.loadError', 'Impossible de charger les versions.'));
    }
  }, [localId, days, demo, t]);

  useEffect(() => {
    setVersionsData(undefined);
    loadVersions();
  }, [loadVersions]);

  const loadEval = useCallback(async () => {
    if (!localId || demo) return;
    const id = encodeURIComponent(localId);
    try {
      const [rulesRes, casesRes, reportsRes] = await Promise.all([
        axios.get(`/api/ai-eval/rules?assistant_id=${id}`, { headers: authHeaders() }),
        axios.get(`/api/ai-eval/cases?assistant_id=${id}&limit=200`, { headers: authHeaders() }),
        axios.get(`/api/ai-eval/runs?assistant_id=${id}&limit=5`, { headers: authHeaders() }),
      ]);
      setEvalData({
        rules: rulesRes.data.rules || [], cases: casesRes.data.cases || [], reports: reportsRes.data.reports || [], error: null,
      });
    } catch (error) {
      setEvalData({ rules: [], cases: [], reports: [], error: t('assistantRules.loadError', 'Impossible de charger les règles et le harnais.') });
    }
  }, [localId, demo, t]);

  useEffect(() => {
    setEvalData({ rules: null, cases: null, reports: null, error: null });
    loadEval();
  }, [loadEval]);

  // ---- editing ----------------------------------------------------------------

  const startEditing = (nextView) => {
    if (!selected) return;
    setEditedName(selected.name || '');
    setEditedPrompt(selected.prompt || '');
    setEditedToolIds(selected.selected_tool_ids || []);
    setSaveError(null);
    setIsEditing(true);
    const target = nextView || (['tools'].includes(view) ? view : 'instructions');
    if (target !== view) updateParams({ tab: target === 'instructions' ? null : target });
  };

  const cancelEditing = () => {
    if (saving) return;
    setIsEditing(false);
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!selected) return;
    if (!editedName.trim()) {
      setSaveError(t('aiAgentsUi.create.nameRequired', 'Le nom est obligatoire.'));
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      if (demo) {
        const updated = { ...selected, name: editedName, prompt: editedPrompt, selected_tool_ids: editedToolIds, updated_at: new Date().toISOString() };
        setAgents((previous) => (previous || []).map((agent) => (agent._id === selected._id ? updated : agent)));
        setSelected(updated);
      } else {
        await axios.put(
          `/api/ai-assistants/${selected._id}`,
          { name: editedName, prompt: editedPrompt, selected_tool_ids: editedToolIds },
          { headers: authHeaders() },
        );
        // The list reloads, and with it the agent's detail (effect below).
        await fetchList();
        loadVersions();
      }
      setIsEditing(false);
      showToast('success', t('aiAgentsUi.agent.saved', 'Agent « {{name}} » enregistré.', { name: editedName.trim() }));
    } catch (error) {
      console.error('Failed to update AI assistant:', error);
      const detail = error.response && error.response.data && error.response.data.detail;
      setSaveError(typeof detail === 'string' ? detail : t('aiAgentsUi.agent.saveError', 'L’enregistrement a échoué. Vos modifications sont conservées.'));
    } finally {
      setSaving(false);
    }
  };

  const handleCopyPrompt = async () => {
    try {
      await navigator.clipboard.writeText((selected && selected.prompt) || '');
      showToast('success', t('aiAgentsUi.agent.copied', 'Instructions copiées.'));
    } catch (error) {
      showToast('danger', t('aiAgentsUi.agent.copyError', 'La copie a échoué.'));
    }
  };

  const errorDetail = (error, fallback) => {
    const detail = error && error.response && error.response.data && error.response.data.detail;
    return typeof detail === 'string' ? detail : fallback;
  };

  // The default agent answers every channel without an agent of its own
  // (ingestion_service.resolve_assistant_id: channel agent, else is_running).
  const runSetDefault = async () => {
    if (!selected) return;
    setActionBusy(true);
    try {
      if (demo) {
        setAgents((previous) => (previous || []).map((agent) => ({ ...agent, is_running: agent._id === selected._id })));
        setSelected((current) => (current ? { ...current, is_running: true } : current));
      } else {
        await axios.put(`/api/ai-assistants/${selected._id}/running`, null, { headers: authHeaders() });
        await fetchList();
      }
      showToast('success', t('settingsPolishUi.agent.defaultDone', '« {{name}} » est maintenant l’agent par défaut.', { name: selected.name }));
    } catch (error) {
      console.error('Failed to set the default agent:', error);
      showToast('danger', errorDetail(error, t('settingsPolishUi.agent.defaultError', 'L’agent par défaut n’a pas pu être changé.')));
    } finally {
      setActionBusy(false);
      setConfirmAction(null);
    }
  };

  const runDelete = async () => {
    if (!selected) return;
    const { _id: id, name } = selected;
    setActionBusy(true);
    try {
      if (!demo) await axios.delete(`/api/ai-assistants/${id}`, { headers: authHeaders() });
      // The remaining agents and no id in the URL: the default (or first) one opens.
      setAgents((previous) => (previous || []).filter((agent) => agent._id !== id));
      updateParams({ assistantId: null, tab: null });
      setConfirmAction(null);
      showToast('success', t('settingsPolishUi.agent.deleted', 'Agent « {{name}} » supprimé.', { name }));
      if (!demo) fetchList();
    } catch (error) {
      console.error('Failed to delete the agent:', error);
      setConfirmAction(null);
      showToast('danger', errorDetail(error, t('settingsPolishUi.agent.deleteError', 'L’agent n’a pas pu être supprimé.')));
    } finally {
      setActionBusy(false);
    }
  };

  // The account's policy of an effectful tool: '' = the registry default.
  // Conditions stored with the policy are sent back as they are.
  const saveToolPolicy = async (name, label, mode) => {
    setPolicySaving(name);
    try {
      if (!demo) {
        await axios.put(
          `/api/ai-proposals/policies/${encodeURIComponent(name)}`,
          { mode: mode || null, conditions: (mode && policyConditions[name]) || {} },
          { headers: authHeaders() },
        );
      }
      setToolPolicies((previous) => {
        const next = { ...previous };
        if (mode) next[name] = mode; else delete next[name];
        return next;
      });
      if (!mode) setPolicyConditions((previous) => { const next = { ...previous }; delete next[name]; return next; });
      showToast('success', t('settingsPolishUi.tools.policySaved', '« {{tool}} » : {{mode}}, pour tous les agents du compte.', {
        tool: label, mode: policyLabel(mode || (policyMeta[name] || {}).default_policy),
      }));
    } catch (error) {
      console.error('Failed to save the tool policy:', error);
      showToast('danger', errorDetail(error, t('tools.policySaveError', 'Impossible d’enregistrer la politique de cet outil.')));
    } finally {
      setPolicySaving(null);
    }
  };

  // ---- derived ----------------------------------------------------------------

  const summary = useMemo(() => summarizeVersions(versionsData), [versionsData]);
  const toolState = useMemo(
    () => agentTools({ selected_tool_ids: isEditing ? editedToolIds : (selected && selected.selected_tool_ids) }, tools),
    [selected, tools, isEditing, editedToolIds],
  );
  const promptBlocks = useMemo(() => parsePromptBlocks(selected && selected.prompt), [selected]);
  const sections = promptBlocks.filter((block) => block.type === 'heading');
  const words = wordCount(selected && selected.prompt);
  const latestReport = (evalData.reports || [])[0] || null;
  const versionEntry = useMemo(() => {
    if (!selected) return null;
    const fromApi = versionsData && (versionsData.versions || []).find((v) => v.prompt_hash === (versionsData.assistant || {}).current_hash);
    return fromApi || currentVersionEntry(selected);
  }, [selected, versionsData]);

  const setView = (next) => updateParams({ tab: next === 'instructions' ? null : next });
  const policyLabel = (mode) => (mode ? t(`tools.policyMode.${mode}`, {
    auto: 'automatique', human: 'validation humaine', proposal: 'validation humaine', forbidden: 'interdit',
  }[mode] || mode) : '');
  const currentDefault = (agents || []).find((agent) => agent.is_running && (!selected || agent._id !== selected._id)) || null;

  // ---- zones ------------------------------------------------------------------

  const attention = [];
  if (selected && !isEditing && !hasInstructions(selected)) {
    attention.push({
      key: 'noprompt', tone: 'warning',
      title: t('aiAgentsUi.agent.noPrompt', 'Cet agent n’a pas d’instructions'),
      detail: t('aiAgentsUi.attention.noPromptDetail', 'Sans instructions, l’agent ne sait pas comment traiter vos tickets.'),
      action: { label: t('aiAgentsUi.agent.writeInstructions', 'Écrire les instructions'), onClick: () => startEditing('instructions') },
    });
  }
  if (latestReport && latestReport.status === 'done' && latestReport.score && latestReport.score.gate_passed === false) {
    attention.push({
      key: 'gate', tone: 'warning',
      title: t('aiAgentsUi.agent.gateFailed', 'Le jeu de référence échoue avec les instructions actuelles'),
      detail: t('aiAgentsUi.agent.gateFailedDetail', '{{rate}} des cas passent (minimum {{min}}) · rejoué le {{date}}', {
        rate: formatPercent(latestReport.score.pass_rate), min: formatPercent(latestReport.score.min_pass), date: formatStamp(latestReport.created_at),
      }),
      action: { label: t('aiAgentsUi.see', 'Voir'), onClick: () => setView('evaluation') },
    });
  }
  if (latestReport && latestReport.status === 'failed') {
    attention.push({
      key: 'reportfailed', tone: 'warning',
      title: t('aiAgentsUi.agent.reportFailed', 'Le dernier rejeu du jeu de référence a échoué'),
      detail: latestReport.error || formatStamp(latestReport.created_at),
      action: { label: t('aiAgentsUi.see', 'Voir'), onClick: () => setView('evaluation') },
    });
  }
  if (selected && toolState.missing.length > 0 && tools.length > 0) {
    attention.push({
      key: 'tools', tone: 'info',
      title: t('aiAgentsUi.agent.toolsMissing', '{{count}} outils choisis ne sont plus proposés à l’agent', { count: toolState.missing.length }),
      detail: t('aiAgentsUi.agent.toolsMissingDetail', 'Supprimés, ou leur source n’est pas configurée sur ce compte : l’agent ne les reçoit pas.'),
      action: { label: t('aiAgentsUi.see', 'Voir'), onClick: () => setView('tools') },
    });
  }

  const renderAttention = () => {
    if (!attention.length) return null;
    return (
      <Card
        icon={TriangleAlert}
        iconTone="warning"
        title={t('aiAgentsUi.attention.title', 'À vérifier')}
        badge={<Badge tone="warning">{attention.length}</Badge>}
        flush
        className="aiam-v2__attention"
      >
        <ul className="u-list">
          {attention.map((item) => (
            <li className="u-row" key={item.key}>
              <Badge tone={item.tone} dot>
                {item.tone === 'info' ? t('aiAgentsUi.attention.review', 'À relire') : t('aiAgentsUi.attention.watch', 'À surveiller')}
              </Badge>
              <div className="u-row__main">
                <div className="u-row__title">{item.title}</div>
                {item.detail && <div className="u-row__meta" title={item.detail}>{item.detail}</div>}
              </div>
              <div className="u-row__side">
                <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={item.action.onClick}>
                  {item.action.label}
                  <ChevronRight aria-hidden="true" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      </Card>
    );
  };

  const renderKpis = () => {
    if (demo || !selected) return null;
    if (versionsData === undefined) {
      return (
        <div className="u-kpi-row aiam-v2__kpis" aria-hidden="true">
          {[0, 1, 2, 3, 4].map((i) => <span key={i} className="u-skeleton aiam-v2__kpi-skeleton" />)}
        </div>
      );
    }
    if (!summary) return null;
    const period = t('aiAgentsUi.kpi.period', 'sur {{days}} jours', { days });
    const score = latestReport && latestReport.score;
    return (
      <div className="u-kpi-row aiam-v2__kpis">
        <Kpi
          icon={Sparkles}
          label={t('aiAgentsUi.kpi.runs', 'Traitements')}
          value={formatCount(summary.runs)}
          context={summary.lastRunAt
            ? t('aiAgentsUi.kpi.runsContext', '{{period}} · dernier : {{when}}', { period, when: formatRelative(summary.lastRunAt) })
            : t('aiAgentsUi.kpi.runsNone', 'aucun {{period}}', { period })}
          onClick={() => setView('versions')}
          title={t('aiAgentsUi.kpi.toVersions', 'Voir par version')}
        />
        <Kpi
          icon={MessagesSquare}
          tone={summary.drafts.rate === null ? 'neutral' : 'success'}
          label={t('aiAgentsUi.kpi.draftsShort', 'Brouillons')}
          value={formatPercent(summary.drafts.rate)}
          context={summary.drafts.reviewed
            ? t('aiAgentsUi.kpi.draftsShortContext', 'approuvés · {{edited}} corrigés sur {{reviewed}}', { edited: formatCount(summary.drafts.edited), reviewed: formatCount(summary.drafts.reviewed) })
            : t('aiAgentsUi.kpi.noneReviewed', 'aucun relu {{period}}', { period })}
          meter={summary.drafts.reviewed ? { value: summary.drafts.approved, max: summary.drafts.reviewed, tone: 'success', label: t('aiAgentsUi.kpi.draftsShort', 'Brouillons') } : undefined}
          onClick={() => setView('versions')}
        />
        <Kpi
          icon={Truck}
          tone={summary.carrier.rate === null ? 'neutral' : 'success'}
          label={t('aiAgentsUi.kpi.carrierShort', 'Transporteur')}
          value={formatPercent(summary.carrier.rate)}
          context={summary.carrier.reviewed
            ? t('aiAgentsUi.kpi.carrierShortContext', 'messages validés · {{reviewed}} relus', { reviewed: formatCount(summary.carrier.reviewed) })
            : t('aiAgentsUi.kpi.noneReviewed', 'aucun relu {{period}}', { period })}
          meter={summary.carrier.reviewed ? { value: summary.carrier.approved, max: summary.carrier.reviewed, tone: 'success', label: t('aiAgentsUi.kpi.carrierShort', 'Transporteur') } : undefined}
          onClick={() => setView('versions')}
        />
        <Kpi
          icon={ListChecks}
          tone="neutral"
          label={t('aiAgentsUi.kpi.overrides', 'Statuts repris')}
          value={formatCount(summary.statusOverrides)}
          context={t('aiAgentsUi.kpi.overridesShortContext', 'par votre équipe, {{period}}', { period })}
        />
        {evalData.reports === null ? (
          <span className="u-skeleton aiam-v2__kpi-skeleton" aria-hidden="true" />
        ) : (
          <Kpi
            icon={FlaskConical}
            tone={score ? (score.gate_passed ? 'success' : 'danger') : 'neutral'}
            label={t('aiAgentsUi.kpi.golden', 'Jeu de référence')}
            value={score ? formatPercent(score.pass_rate) : '—'}
            context={score
              ? t('aiAgentsUi.kpi.goldenContext', '{{passed}}/{{replayed}} cas · {{date}}', { passed: score.passed, replayed: score.replayed, date: formatShortDay(latestReport.created_at) })
              : (evalData.cases || []).length
                ? t('aiAgentsUi.kpi.goldenNever', '{{count}} cas, jamais rejoués', { count: evalData.cases.length })
                : t('aiAgentsUi.kpi.goldenNone', 'aucun cas de référence')}
            contextTone={score && !score.gate_passed ? 'danger' : undefined}
            meter={score && score.replayed ? { value: score.passed, max: score.replayed, tone: score.gate_passed ? 'success' : 'danger', label: t('aiAgentsUi.kpi.golden', 'Jeu de référence') } : undefined}
            onClick={() => setView('evaluation')}
            active={view === 'evaluation'}
          />
        )}
      </div>
    );
  };

  const renderInstructions = () => {
    if (isEditing) {
      return (
        <Card
          icon={Pencil}
          title={t('aiAgentsUi.agent.editTitle', 'Nom et instructions')}
          description={t('aiAgentsUi.agent.editDesc', 'Enregistrer crée une nouvelle version. Pour mesurer un changement avant de l’appliquer : Versions › Évaluer un changement.')}
        >
          <div className="u-form">
            {saveError && <Banner tone="danger">{saveError}</Banner>}
            <Field label={t('aiAssistant.name', 'Nom')} htmlFor="name">
              <input id="name" className="u-input aiam-v2__name-input" value={editedName} onChange={(e) => setEditedName(e.target.value)} disabled={saving} />
            </Field>
            <Field
              label={t('aiAgentsUi.agent.instructions', 'Instructions')}
              htmlFor="aiam-prompt"
              help={t('aiAgentsUi.agent.wordCount', '{{value}} mots', { count: wordCount(editedPrompt), value: formatCount(wordCount(editedPrompt)) })}
            >
              <textarea
                id="aiam-prompt"
                className="u-textarea aiam-v2__prompt-input"
                value={editedPrompt}
                onChange={(e) => setEditedPrompt(e.target.value)}
                placeholder={t('aiAgentsUi.agent.promptPlaceholder', 'Votre métier, le ton, ce que l’agent fait et quand il passe la main.')}
                disabled={saving}
              />
            </Field>
          </div>
        </Card>
      );
    }
    const responds = [selected.associated_email, ...activeChannels(selected).map((name) => channelLabel(t, name))].filter(Boolean);
    return (
      <div className="aiam-v2__doc-layout">
        <Card
          icon={ScrollText}
          title={t('aiAgentsUi.agent.instructions', 'Instructions')}
          badge={words > 0 ? <Badge>{t('aiAgentsUi.agent.wordCount', '{{value}} mots', { count: words, value: formatCount(words) })}</Badge> : null}
          actions={hasInstructions(selected) ? (
            <button type="button" className="u-btn u-btn--ghost u-btn--sm" onClick={handleCopyPrompt}>
              <Copy aria-hidden="true" />
              {t('aiAssistant.copyPrompt', 'Copier')}
            </button>
          ) : null}
          className="aiam-v2__doc-card"
        >
          {hasInstructions(selected) ? (
            <div className="aiam-v2__doc">{promptBlocks.map(renderBlock)}</div>
          ) : (
            <EmptyState
              icon={ScrollText}
              title={t('aiAgentsUi.agent.noPrompt', 'Cet agent n’a pas d’instructions')}
              action={(
                <button type="button" className="u-btn u-btn--primary u-btn--sm" onClick={() => startEditing('instructions')}>
                  <Pencil aria-hidden="true" />
                  {t('aiAgentsUi.agent.writeInstructions', 'Écrire les instructions')}
                </button>
              )}
            >
              {t('aiAgentsUi.agent.noPromptHint', 'Décrivez votre métier, le ton à tenir et quand passer la main à votre équipe.')}
            </EmptyState>
          )}
        </Card>
        <aside className="aiam-v2__side">
          {sections.length > 1 && (
            <Card title={t('aiAgentsUi.agent.sections', 'Sommaire')} flush>
              <nav className="aiam-v2__toc" aria-label={t('aiAssistant.promptSections', 'Sections')}>
                {sections.map((section) => (
                  <button
                    key={section.num}
                    type="button"
                    className="aiam-v2__toc-item"
                    onClick={() => {
                      const element = document.getElementById(`prompt-sec-${section.num}`);
                      if (element && element.scrollIntoView) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                  >
                    <span className="aiam-v2__toc-num u-num">{section.num}</span>
                    <span className="aiam-v2__toc-text">{section.text.toLowerCase()}</span>
                  </button>
                ))}
              </nav>
            </Card>
          )}
          <Card title={t('aiAgentsUi.agent.facts', 'Fiche')}>
            <DescriptionList
              items={[
                {
                  label: t('aiAgentsUi.agent.role', 'Rôle'),
                  value: selected.is_running
                    ? t('aiAgentsUi.agent.roleDefault', 'Agent par défaut : reçoit tout ce qui n’a pas d’agent attitré')
                    : t('aiAgentsUi.agent.roleAssigned', 'Agent attitré à ses canaux'),
                },
                { label: t('aiAgentsUi.agent.responds', 'Répond sur'), value: responds.length ? responds.join(' · ') : null },
                {
                  label: t('aiAgentsUi.agent.version', 'Version'),
                  value: versionEntry && versionEntry.version
                    ? t('aiAgentsUi.agent.versionValue', 'v{{version}} · {{date}}', { version: versionEntry.version, date: formatShortDay(versionEntry.changed_at) })
                    : null,
                },
                { label: t('aiAgentsUi.agent.created', 'Créé le'), value: formatShortDay(selected.created_at) || null },
              ]}
            />
            <p className="u-help aiam-v2__facts-help">
              <Link className="u-link" to="/my-ai-assistants">{t('aiAgentsUi.agent.bindingsLink', 'Boîte mail, canaux et marque : depuis la liste des agents')}</Link>
            </p>
          </Card>
        </aside>
      </div>
    );
  };

  // The structured rules stay editable behind their disclosure; with none
  // (and none can be added any more) there is nothing to show.
  const renderRules = () => (!evalData.error && Array.isArray(evalData.rules) && evalData.rules.length === 0 ? null : (
    <section className="u-card aiam-v2__rules">
      <details className="u-disclosure">
        <summary>
          <ChevronRight aria-hidden="true" />
          {t('aiAgentsUi.rules.title', 'Règles structurées')}
          {evalData.rules && <Badge>{formatCount(evalData.rules.length)}</Badge>}
        </summary>
        <div className="aiam-v2__rules-body">
          <AssistantRules rules={evalData.rules} error={evalData.error} onReload={loadEval} />
        </div>
      </details>
    </section>
  ));

  const renderTools = () => {
    const missingBanner = toolState.missing.length > 0 && tools.length > 0 && (
      <Banner
        tone="info"
        title={t('aiAgentsUi.agent.toolsMissing', '{{count}} outils choisis ne sont plus proposés à l’agent', { count: toolState.missing.length })}
        actions={isEditing ? (
          <button
            type="button"
            className="u-btn u-btn--secondary u-btn--sm"
            onClick={() => setEditedToolIds((previous) => previous.filter((id) => !toolState.missing.includes(id)))}
          >
            {t('aiAgentsUi.agent.removeMissing', 'Les retirer')}
          </button>
        ) : null}
      >
        {t('aiAgentsUi.agent.toolsMissingDetail', 'Supprimés, ou leur source n’est pas configurée sur ce compte : l’agent ne les reçoit pas.')}
        <span className="aiam-v2__codes">{toolState.missing.map((id) => <code key={id} className="u-code">{id}</code>)}</span>
      </Banner>
    );
    if (isEditing) {
      return (
        <Card
          icon={Wrench}
          title={t('aiAgentsUi.agent.toolsEditTitle', 'Choisir les outils')}
          description={t('aiAgentsUi.agent.toolsDesc', 'Ce que l’agent peut faire sur un ticket. Lire les fichiers, changer le statut et passer la main à votre équipe font partie de tout agent.')}
        >
          <div className="u-form">
            {missingBanner}
            <ToolChecklist tools={tools} selectedIds={editedToolIds} onChange={setEditedToolIds} disabled={saving} />
          </div>
        </Card>
      );
    }
    const groups = groupTools(toolState.selected);
    return (
      <Card
        icon={Wrench}
        title={t('aiAgentsUi.agent.toolsTitle', 'Outils de l’agent')}
        badge={<Badge>{formatCount(toolState.selected.length)}</Badge>}
        description={[
          t('aiAgentsUi.agent.toolsDesc', 'Ce que l’agent peut faire sur un ticket. Lire les fichiers, changer le statut et passer la main à votre équipe font partie de tout agent.'),
          Object.keys(policyMeta).length > 0 && t('settingsPolishUi.tools.policyDesc', 'Pour un outil qui agit : automatique, validation humaine ou interdit — ce choix vaut pour tous les agents du compte.'),
        ].filter(Boolean).join(' ')}
        actions={(
          <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => startEditing('tools')}>
            <Pencil aria-hidden="true" />
            {t('aiAgentsUi.agent.editTools', 'Modifier les outils')}
          </button>
        )}
        flush
      >
        {missingBanner && <div className="aiam-v2__pad">{missingBanner}</div>}
        {!groups.length ? (
          <EmptyState icon={Wrench} title={t('aiAgentsUi.agent.noTools', 'Aucun outil choisi')}>
            {t('aiAgentsUi.agent.noToolsHint', 'L’agent répond, lit les fichiers et choisit le statut ; ajoutez des outils pour qu’il agisse (suivi, transporteurs, commandes).')}
          </EmptyState>
        ) : groups.map(({ group, tools: list }) => (
          <section key={group} className="aiam-v2__tool-group">
            <h3 className="u-overline aiam-v2__tool-group-title">{toolGroupMeta(t, group).title}</h3>
            <ul className="u-list">
              {list.map((tool) => {
                const name = toolFunctionName(tool);
                const label = toolLabel(t, name);
                const description = toolDescription(tool);
                const meta = policyMeta[name];
                const selectId = `aiam-policy-${tool.id}-${name}`;
                return (
                  <li key={`${tool.id}-${name}`} className="u-row aiam-v2__tool">
                    <div className="u-row__main">
                      <div className="u-row__title">
                        {label}
                        {label !== name && <code className="u-code">{name}</code>}
                      </div>
                      {description && <div className="u-row__meta" title={description}>{description}</div>}
                    </div>
                    <div className="u-row__side aiam-v2__tool-badges">
                      <ToolBadges tool={tool} mode={toolPolicies[name]} hidePolicy={Boolean(meta)} />
                      {meta && (
                        <>
                          <label className="u-sr-only" htmlFor={selectId}>
                            {t('settingsPolishUi.tools.policyFor', 'Quand l’agent appelle « {{tool}} »', { tool: label })}
                          </label>
                          <select
                            id={selectId}
                            className="u-select aiam-v2__policy"
                            value={toolPolicies[name] || ''}
                            disabled={policySaving === name}
                            title={t('tools.policyHint', 'Quand l’IA appelle cet outil : exécuter, demander votre validation, ou refuser.')}
                            onChange={(e) => saveToolPolicy(name, label, e.target.value)}
                          >
                            <option value="">{t('tools.policyDefault', 'Par défaut ({{mode}})', { mode: policyLabel(meta.default_policy) })}</option>
                            <option value="auto">{policyLabel('auto')}</option>
                            {(meta.human_allowed || toolPolicies[name] === 'human') && <option value="human">{policyLabel('human')}</option>}
                            <option value="forbidden">{policyLabel('forbidden')}</option>
                          </select>
                        </>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </Card>
    );
  };

  // ---- page -------------------------------------------------------------------

  const pageLoading = user === undefined || agents === null || (agents.length > 0 && !selected && !missing);

  const breadcrumb = (
    <>
      <Link to="/my-ai-assistants">{t('aiAgentsUi.list.title', 'Agents IA')}</Link>
      <ChevronRight aria-hidden="true" className="aiam-v2__crumb-sep" />
    </>
  );

  if (pageLoading) {
    return (
      <div className="u-page aiam-v2" aria-busy="true">
        <PageHeader breadcrumb={breadcrumb} title={<span className="u-skeleton aiam-v2__title-skeleton" />} />
        <div className="u-well">
          <div className="u-kpi-row aiam-v2__kpis" aria-hidden="true">
            {[0, 1, 2, 3, 4].map((i) => <span key={i} className="u-skeleton aiam-v2__kpi-skeleton" />)}
          </div>
          <span className="u-skeleton aiam-v2__card-skeleton" aria-hidden="true" />
        </div>
      </div>
    );
  }

  if (!agents.length || missing || !selected) {
    return (
      <div className="u-page aiam-v2">
        <PageHeader breadcrumb={breadcrumb} title={t('aiAgentsUi.agent.missingTitle', 'Agent IA')} />
        <div className="u-well">
          <Card>
            <EmptyState
              icon={Bot}
              title={agents.length
                ? t('aiAgentsUi.agent.missing', 'Cet agent n’existe pas ou plus')
                : t('aiAgentsUi.list.emptyTitle', 'Aucun agent pour l’instant')}
              action={(
                <Link className="u-btn u-btn--primary u-btn--sm" to="/my-ai-assistants">
                  {agents.length ? t('aiAgentsUi.agent.backToList', 'Voir les agents') : t('aiAgentsUi.list.create', 'Créer un agent')}
                </Link>
              )}
            >
              {agents.length
                ? t('aiAgentsUi.agent.missingHint', 'Il a peut-être été supprimé. Choisissez un agent dans la liste.')
                : t('aiAgentsUi.list.emptyHint', 'Créez un agent : il lira vos tickets, répondra, contactera les transporteurs et choisira le statut avec ses outils.')}
            </EmptyState>
          </Card>
        </div>
      </div>
    );
  }

  const subtitleParts = [];
  if (versionEntry && versionEntry.version) {
    const actor = versionEntry.actor && (versionEntry.actor.name || versionEntry.actor.email);
    subtitleParts.push(actor
      ? t('aiAgentsUi.agent.subtitleVersionBy', 'Version {{version}} du {{date}}, par {{actor}}', { version: versionEntry.version, date: formatShortDay(versionEntry.changed_at), actor })
      : t('aiAgentsUi.agent.subtitleVersion', 'Version {{version}} du {{date}}', { version: versionEntry.version, date: formatShortDay(versionEntry.changed_at) }));
  }
  subtitleParts.push(t('aiAgentsUi.list.tools', '{{count}} outils', { count: toolState.selected.length }));

  return (
    <div className="u-page aiam-v2">
      <PageHeader
        breadcrumb={breadcrumb}
        title={(
          <span className="aiam-v2__title">
            <span className="aiam-v2__title-text">{selected.name}</span>
            {selected.is_running && <Badge tone="accent">{t('aiAgentsUi.default', 'Par défaut')}</Badge>}
          </span>
        )}
        subtitle={subtitleParts.join(' · ')}
        actions={isEditing ? (
          <>
            <button type="button" className="u-btn u-btn--secondary" onClick={cancelEditing} disabled={saving}>
              {t('common.cancel', 'Annuler')}
            </button>
            <button type="button" className="u-btn u-btn--primary" data-tour="aiam-save" onClick={handleSave} disabled={saving || !editedName.trim()}>
              <Check aria-hidden="true" />
              {saving ? t('common.saving', 'Enregistrement...') : t('common.save', 'Enregistrer')}
            </button>
          </>
        ) : (
          <>
            <OverflowMenu
              label={t('settingsPolishUi.agent.moreActions', 'Plus d’actions sur l’agent')}
              items={[
                !selected.is_running && { label: t('settingsPolishUi.agent.setDefault', 'Définir comme agent par défaut'), icon: Star, onClick: () => setConfirmAction('default') },
                !selected.is_running && { separator: true },
                { label: t('settingsPolishUi.agent.delete', 'Supprimer l’agent'), icon: Trash2, danger: true, onClick: () => setConfirmAction('delete') },
              ]}
            />
            <Link className="u-btn u-btn--secondary" to={`/knowledge-board?agent=${selected._id}`}>
              <BookOpen aria-hidden="true" />
              {t('aiAssistant.knowledgeBoard', 'Connaissances')}
            </Link>
            <button type="button" className="u-btn u-btn--primary" data-tour="aiam-edit" onClick={() => startEditing()}>
              <Pencil aria-hidden="true" />
              {t('aiAssistant.edit', 'Modifier')}
            </button>
          </>
        )}
      />

      <div className="u-well">
        {isEditing && (
          <Banner tone="info" title={t('aiAgentsUi.agent.editingTitle', 'Modification en cours')}>
            {t('aiAgentsUi.agent.editingHint', 'Nom, instructions et outils : enregistrez en haut de la page pour créer la nouvelle version.')}
          </Banner>
        )}
        {renderAttention()}
        {renderKpis()}
        <Tabs
          label={selected.name}
          active={view}
          onChange={setView}
          tabs={[
            { key: 'instructions', label: t('aiAgentsUi.agent.instructions', 'Instructions'), icon: ScrollText },
            { key: 'tools', label: t('aiAgentsUi.agent.tools', 'Outils'), icon: Wrench, count: formatCount(toolState.selected.length) },
            ...(demo ? [] : [
              { key: 'versions', label: t('aiAgentsUi.agent.versions', 'Versions'), icon: History, count: summary ? formatCount(summary.versionCount) : undefined },
              { key: 'evaluation', label: t('aiAgentsUi.kpi.golden', 'Jeu de référence'), icon: FlaskConical, count: evalData.cases ? formatCount(evalData.cases.length) : undefined },
            ]),
          ]}
        />
        <div role="tabpanel" className="aiam-v2__panel">
          {view === 'instructions' && (
            <>
              {renderInstructions()}
              {!demo && !isEditing && renderRules()}
            </>
          )}
          {view === 'tools' && renderTools()}
          {view === 'versions' && !demo && (
            <AssistantVersions
              assistantId={localId}
              currentInstructions={selected.prompt}
              data={versionsData}
              error={versionsError}
              days={days}
              onDaysChange={(next) => updateParams({ days: next === DEFAULT_DAYS ? null : next })}
              onReload={loadVersions}
              onApplied={() => { fetchList(); loadEval(); }}
            />
          )}
          {view === 'evaluation' && !demo && (
            <AssistantHarness assistantId={localId} cases={evalData.cases} reports={evalData.reports} onReload={loadEval} />
          )}
        </div>
      </div>

      {confirmAction === 'default' && (
        <ConfirmDialog
          icon={Star}
          title={t('settingsPolishUi.agent.defaultTitle', 'Définir « {{name}} » comme agent par défaut ?', { name: selected.name })}
          message={[
            t('settingsPolishUi.agent.defaultMessage', 'L’agent par défaut répond sur tous les canaux qui n’ont pas d’agent attitré.'),
            currentDefault
              ? t('settingsPolishUi.agent.defaultReplaces', 'Il remplace « {{name}} », qui garde ses canaux attitrés.', { name: currentDefault.name })
              : t('settingsPolishUi.agent.defaultNone', 'Aujourd’hui, aucun agent ne répond sur ces canaux.'),
          ].join(' ')}
          confirmLabel={t('settingsPolishUi.agent.setDefaultConfirm', 'Définir par défaut')}
          cancelLabel={t('common.cancel', 'Annuler')}
          busy={actionBusy}
          onCancel={() => !actionBusy && setConfirmAction(null)}
          onConfirm={runSetDefault}
        />
      )}
      {confirmAction === 'delete' && (
        <ConfirmDialog
          icon={Trash2}
          title={t('settingsPolishUi.agent.deleteTitle', 'Supprimer l’agent « {{name}} » ?', { name: selected.name })}
          message={[
            t('settingsPolishUi.agent.deleteMessage', 'Suppression définitive : son nom, ses instructions et ses outils sont perdus. Ses sources de connaissances restent sur le compte, sous « Sans agent ».'),
            selected.is_running
              ? t('settingsPolishUi.agent.deleteDefault', 'C’est l’agent par défaut : les canaux sans agent attitré n’auront plus d’agent tant que vous n’en désignez pas un autre.')
              : t('settingsPolishUi.agent.deleteAssigned', 'Ses canaux attitrés passent à l’agent par défaut.'),
          ].join(' ')}
          confirmLabel={t('settingsPolishUi.agent.deleteConfirm', 'Supprimer définitivement')}
          cancelLabel={t('common.cancel', 'Annuler')}
          busy={actionBusy}
          onCancel={() => !actionBusy && setConfirmAction(null)}
          onConfirm={runDelete}
        />
      )}
      {toast && (
        <Toast tone={toast.tone} onClose={() => setToast(null)} closeLabel={t('common.close', 'Fermer')}>
          {toast.text}
        </Toast>
      )}
    </div>
  );
};

export default AIAssistantManagement;
